import { __decorate } from "tslib";
import { Component, Mixins, Prop } from 'vue-property-decorator';
import FbShopBusinesses from '@/plugins/graphql/queries/v2/fb-shop-businesses.gql';
import FbShopCatalogs from '@/plugins/graphql/queries/v2/fb-shop-catalogs.gql';
import RouterParamsAppMixin from '@/helpers/vue_mixins/RouterParamsAppMixin';
import { FbGraphGenericModel } from '@/helpers/entities/models/FbGraphGenericModel';
import AppActivateFbShop from '@/plugins/graphql/mutations/v2/app-activate-fb-shop.gql';
import AppDeactivateFbShop from '@/plugins/graphql/mutations/v2/app-deactivate-fb-shop.gql';
import FbShopSyncAllProducts from '@/plugins/graphql/mutations/v2/app-fb-shop-sync-all.gql';
import AppBasicFragment from '@/plugins/graphql/fragments/v2/app-basic.gql';
import FormMixin from '@/helpers/vue_mixins/FormMixin';
import AppBasicDetails from '@/components/v2/AppBasicDetails';
import moment from 'moment';
import ActionButton from '@/components/action_button/Index.vue';
import ViewportSizeMixin from '@/helpers/mixins/ViewportSizeMixin';
import FbShopDeactivationDialog from '@/components/v2/FbShopDeactivationDialog.vue';
import ButtonLoaderMixin from '@/helpers/vue_mixins/ButtonLoaderMixin';
import CleverTapEventsMixin from '@/helpers/vue_mixins/v3/CleverTapEventsMixin';
let FbShopActivationForm = class FbShopActivationForm extends Mixins(RouterParamsAppMixin, FormMixin, ViewportSizeMixin, ButtonLoaderMixin, CleverTapEventsMixin) {
    constructor() {
        super(...arguments);
        this.connectedToFbShop = false;
        this.fbShopBusinesses = [];
        this.fbShopCatalogs = [];
        this.displayCatalogSelect = false;
        this.selectedBusiness = null;
        this.startSync = false;
        this.form = {
            catalog: null,
            business: null,
        };
        this.polling = true;
        this.pollTimeoutId = null;
    }
    connectToFbShop() {
        this.connectedToFbShop = !this.connectedToFbShop;
    }
    toggleDeactivationDialog() {
        const vm = this;
        vm.$refs.deactivationDialog.showDialog();
    }
    displayCatalogSelection() {
        this.displayCatalogSelect = true;
        // Will run the query upon selecting Business ID
        // Reference: https://apollo.vuejs.org/api/smart-query.html#skip
        this.$apollo.queries.fbShopCatalogs.skip = false;
        // Refetch FB Shop Catalog query upon selecting Business ID
        // Reference: https://apollo.vuejs.org/api/smart-query.html#refetch
        this.$apollo.queries.fbShopCatalogs.refetch();
    }
    async activateFbShop(form) {
        this.fbCatalogAction('ACTIVATE AND SYNC FACEBOOK CATALOG');
        if (!form.catalog) {
            throw new Error('Please select a catalog before proceeding');
        }
        const appUuid = this.appUUID;
        const { catalog, business } = form;
        if (catalog && business) {
            const catalogParams = {
                catalogId: catalog.id,
                catalogName: catalog.name,
                businessId: business.id,
                businessName: business.name,
            };
            await this.$apollo.mutate({
                mutation: AppActivateFbShop,
                variables: {
                    input: {
                        appUuid,
                        catalogParams,
                    },
                },
                update: (cache, { data }) => {
                    const appBasicFrag = {
                        id: data.ecommerceFbShopActivate.app.uuid,
                        fragment: AppBasicFragment,
                        fragmentName: 'AppBasic',
                    };
                    const appBasic = cache.readFragment(appBasicFrag);
                    cache.writeFragment({
                        ...appBasicFrag,
                        data: appBasic,
                    });
                },
            });
        }
    }
    async deactivateFbShop() {
        const appUuid = this.appUUID;
        await this.$apollo.mutate({
            mutation: AppDeactivateFbShop,
            variables: {
                input: {
                    appUuid,
                },
            },
            update: (cache, { data }) => {
                const appBasicFrag = {
                    id: data.fbShopDeactivate.app.uuid,
                    fragment: AppBasicFragment,
                    fragmentName: 'AppBasic',
                };
                const appBasic = cache.readFragment(appBasicFrag);
                cache.writeFragment({
                    ...appBasicFrag,
                    data: appBasic,
                });
            },
        });
    }
    async syncAllProduct() {
        this.fbCatalogAction('SYNC FACEBOOK CATALOG');
        const appUuid = this.appUUID;
        await this.$apollo.mutate({
            mutation: FbShopSyncAllProducts,
            variables: {
                input: {
                    appUuid,
                },
            },
            update: (cache, { data }) => {
                const appBasicFrag = {
                    id: data.productsSyncAll.app.uuid,
                    fragment: AppBasicFragment,
                    fragmentName: 'AppBasic',
                };
                const appBasic = cache.readFragment(appBasicFrag);
                cache.writeFragment({
                    ...appBasicFrag,
                    data: appBasic,
                });
            },
        });
    }
    async disconnectToFbShop() {
        this.fbCatalogAction('DISCONNECT FACEBOOK CATALOG');
        const vm = this;
        await this.deactivateFbShop()
            .then(() => {
            this.successNotification(`Facebook Shop Deactivated Successfully`);
        })
            .catch((e) => {
            this.errorNotification(e.message);
            this.logError(e.message);
        })
            .finally(() => {
            vm.$refs.deactivationDialog.hideDialog();
            this.connectedToFbShop = false;
            this.refetch();
        });
    }
    async submitForm(connected) {
        this.startSync = true;
        this.requestOnFlight();
        const msg = connected ? 'Product Catalog Synced' : 'Facebook Shop Activated';
        const mutation = () => (connected ? this.syncAllProduct() : this.activateFbShop(this.form));
        await mutation()
            .then(() => {
            this.successNotification(`${msg} Successfully`);
            this.requestDone();
            this.startSync = false;
            this.polling = true;
            this.refetch();
            this.startPolling();
        })
            .catch((e) => {
            if (e.message === 'Network error: Failed to fetch') {
                this.successNotification(`Your Catalog is currently syncing`);
            }
            else {
                this.errorNotification(e.message);
            }
            this.requestDone();
            this.startSync = false;
            this.polling = true;
            this.refetch();
            this.startPolling();
            this.logError(e.message);
        });
    }
    lastSyncTextDetails(date) {
        return `Facebook Catalog is updated as of ${moment(date).format('MMMM DD, YYYY hh:mm a')}`;
    }
    get isCatalogSyncing() {
        return 'Catalog is Currently Syncing';
    }
    displayText(connected, text, defaultText) {
        if (connected) {
            return text;
        }
        return defaultText;
    }
    syncButtonText(connected) {
        if (connected) {
            return 'Sync All';
        }
        return 'Connect to FB Catalog';
    }
    startPolling() {
        this.pollingRefetch();
    }
    stopPolling() {
        if (this.pollTimeoutId) {
            clearTimeout(this.pollTimeoutId);
            this.pollTimeoutId = null;
        }
    }
    async pollingRefetch() {
        this.refetch();
        if (this.polling) {
            this.pollTimeoutId = setTimeout(() => {
                this.startPolling();
                this.polling = this.app.catalogSyncing;
            }, 60000);
        }
        else {
            // Stop polling if catalogSyncing is false
            this.stopPolling();
        }
    }
    mounted() {
        this.startPolling();
    }
};
__decorate([
    Prop({ type: String, required: true })
], FbShopActivationForm.prototype, "appUuid", void 0);
__decorate([
    Prop({ type: Object, default: () => ({}) })
], FbShopActivationForm.prototype, "app", void 0);
__decorate([
    Prop({ type: Function })
], FbShopActivationForm.prototype, "refetch", void 0);
FbShopActivationForm = __decorate([
    Component({
        components: {
            AppBasicDetails,
            ActionButton,
            FbShopDeactivationDialog,
        },
        apollo: {
            fbShopBusinesses: {
                query: FbShopBusinesses,
                variables() {
                    return {
                        appUuid: this.appUUID,
                    };
                },
                update(data) {
                    const businesses = data.fbBusinessesByApp.edges;
                    const businessNode = (business) => {
                        return new FbGraphGenericModel(business.node);
                    };
                    return businesses.map(businessNode);
                },
                error(error) {
                    this.errorNotification(error.message);
                    // Handle the error appropriately, e.g., set an error state, show a message to the user, etc.
                },
            },
            fbShopCatalogs: {
                query: FbShopCatalogs,
                variables() {
                    return {
                        appUuid: this.appUUID,
                        businessId: this.form.business.id,
                    };
                },
                // Will skip this query in component load
                skip() {
                    return true;
                },
                update(data) {
                    const catalogs = data.fbCatalogsByBusinessId.edges;
                    const catalogNode = (catalog) => {
                        return new FbGraphGenericModel(catalog.node);
                    };
                    return catalogs.map(catalogNode);
                },
                error(error) {
                    this.errorNotification(error.message);
                    // Handle the error appropriately, e.g., set an error state, show a message to the user, etc.
                },
            },
        },
    })
], FbShopActivationForm);
export default FbShopActivationForm;
